import { Box, Button, FormikSelectInputNative, FormikTextInput, Toggle } from '@palmetto/palmetto-components';
import { Field, Formik, FormikHelpers } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { Locale, ProgramType, ProgramTypeLabels } from 'types';

import { isSalesRepLicenseManagedByRecheck } from '../../helpers/salesRepRecheck';
import { useGetCurrentUserQuery } from '../../services/users';
import { UtilityNameSelectInput } from '../Account/UtilityNameSelectInput';
import { AddressFormFields } from '../AddressFormFields/AddressFormFields';
import { useCanPerformActionForNoNewSalesOrgStatus } from '../auth/canPerformActionForNoNewSalesOrgStatus';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { RecheckLicenseLink } from '../SalesRepVerification/RecheckLicenseLink';

import { ApplicantInformation } from './ApplicantInformation';

const applicantSchema = yup.object().shape({
  firstName: yup.string().required('First Name is a required field.'),
  middleName: yup.string(),
  lastName: yup.string().required('Last Name is a required field.'),
  phoneNumber: yup
    .string()
    .required('Phone Number is a required field.')
    .test('phone number test', 'Phone Number is a required field.', (value) => value !== '+1'),
  email: yup.string().required('Email is a required field.').email('Email is not a valid email.'),
});

const getAccountSchema = () =>
  yup.object().shape({
    addAdditionalApplicant: yup.boolean().optional().nullable(),
    address1: yup
      .string()
      .required('Address 1 is a required field.')
      .max(60, 'Address Line 1 must be 60 characters or fewer.'),
    address2: yup.string().max(60, 'Address Line 2 must be 60 characters or fewer.'),
    city: yup.string().required('City is a required field.'),
    state: yup.string().required('State is a required field.'),
    zip: yup
      .string()
      .required('Zip Code is a required field.')
      .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits.'),
    lat: yup.number().when('state', {
      is: 'PR',
      then: (schema) => schema.required('Latitude is a required field.'),
    }),
    lon: yup.number().when('state', {
      is: 'PR',
      then: (schema) => schema.required('Longitude is a required field.'),
    }),
    // applicant info
    primary: applicantSchema,
    secondary: yup.object().when('addAdditionalApplicant', {
      is: true,
      then: () => applicantSchema,
    }),
    applicantAddress1: yup
      .string()
      .required('Address 1 is a required field.')
      .max(60, 'Address Line 1 must be 60 characters or fewer.'),
    applicantAddress2: yup.string().max(60, 'Address Line 2 must be 60 characters or fewer.'),
    applicantCity: yup.string().required('City is a required field.'),
    applicantState: yup.string().required('State is a required field.'),
    applicantZip: yup
      .string()
      .required('Zip Code is a required field.')
      .matches(/^\d{5}$/, 'Zip Code must be exactly 5 digits.'),
    // sales rep info
    salesRepName: yup.string().when('programType', {
      is: ProgramType.consumerSolar,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required('Sales Representative Name is a required field.'),
    }),
    salesRepLicenseNumber: yup.string(),
    salesRepEmail: yup.string().when('programType', {
      is: ProgramType.consumerSolar,
      then: (schema) => schema.optional().email('Sales Rep Email is not a valid email.'),
      otherwise: (schema) =>
        schema.required('Sales Rep Email is a required field.').email('Sales Rep Email is not a valid email.'),
    }),
    salesRepPhoneNumber: yup.string(),
    reference: yup.string(),
    programReferenceId: yup.string(),
    programType: yup.string().required('Program Type is a required field.').oneOf(Object.values(ProgramType)),
    language: yup.string().oneOf(Object.values(Locale)),
    // hvac property details
    homeSize: yup.number().nullable().min(1, 'Home Size (sq ft) must be at least 1'),
    existingSystems: yup.number().nullable().min(0, 'Number of Existing Systems must be at least 0'),
    systemsBeingReplaced: yup
      .number()
      .nullable()
      .min(1, 'Number of Systems Being Installed must be at least 1')
      .max(10, 'Number of Systems Being Installed cannot be more than 10'),
    // generator-only property detail (per PRD)
    numberOfSystems: yup
      .number()
      .nullable()
      .min(1, 'Number of Systems must be at least 1')
      .max(10, 'Number of Systems cannot be more than 10'),
    // utility info
    lseId: yup.number().when('programType', {
      is: (val: string) => val === 'hvac' || val === 'waterHeater' || val === 'generator' || val === 'geothermal',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required('Utility Name is a required field.'),
    }),
    tariffId: yup.number().nullable(),
    rate: yup.number().nullable(),
  });

export type AccountFormSchema = yup.InferType<ReturnType<typeof getAccountSchema>> & {
  secondary?: yup.InferType<typeof applicantSchema>;
};
type SubmitHandler = (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<any>;

export const AccountForm = ({
  initialValues,
  handleSubmit,
  disabledFields = [],
  hasAdditionalApplicantData,
  programTypes = [],
  isAccountCreate = false,
}: {
  initialValues: any;
  handleSubmit: SubmitHandler;
  disabledFields?: string[] | undefined;
  hasAdditionalApplicantData: boolean;
  programTypes: ProgramType[];
  isAccountCreate?: boolean;
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();
  const { profileAsSalesRep: profileAsSalesRepFeatureEnabled = false, salesRepRecheckBeforeContract } = useFlags();
  const accountSchema = getAccountSchema();
  const [showAdditionalApplicant, setShowAdditionalApplicant] = useState(hasAdditionalApplicantData);
  const allowsAction = useIsOrgReadOnly();
  const { data: user } = useGetCurrentUserQuery();

  const canPerformActionBasedOnNoNewSalesOrgStatus = useCanPerformActionForNoNewSalesOrgStatus();

  let programTypeDisabled = false;
  const shownProgramTypes = programTypes.map((t) => ({ label: ProgramTypeLabels[t], value: t }));
  if (shownProgramTypes.length === 1) {
    initialValues.programType = shownProgramTypes[0].value;
    programTypeDisabled = true;
  }

  if (isAccountCreate && ![ProgramType.newCommunity, ProgramType.newHomes].includes(initialValues.programType)) {
    initialValues.usePropAddressForMainApplicantAddress = true;
  }

  const copyPropertyAddressToApplicant = (values: any, setFieldValue: any) => {
    setFieldValue('applicantAddress1', values.address1 || '');
    setFieldValue('applicantAddress2', values.address2 || '');
    setFieldValue('applicantCity', values.city || '');
    setFieldValue('applicantState', values.state || '');
    setFieldValue('applicantZip', values.zip || '');
  };

  return (
    <>
      <Formik
        validationSchema={accountSchema}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={(values, formikHelpers) => {
          if (isSalesRepLicenseManagedByRecheck(values.state, values.programType, salesRepRecheckBeforeContract)) {
            // Recheck owns the license, so the form must not send a value for it.
            const { salesRepLicenseNumber: _omitted, ...rest } = values;
            return handleSubmit(rest, formikHelpers);
          }
          return handleSubmit(values, formikHelpers);
        }}
        enableReinitialize={true}
      >
        {({ isSubmitting, setFieldValue, values }) => {
          return (
            <EnhancedFormikForm noValidate>
              <FormSection title="Account">
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  childGap={{ base: 'lg', desktop: 'xl' }}
                >
                  <Field
                    label="Language"
                    name="language"
                    id="language"
                    options={Object.values(Locale).map((l) => ({ label: l, value: l }))}
                    component={FormikSelectInputNative}
                    autoComplete="off"
                    isDisabled={isSubmitting || disabledFields.includes('language')}
                  />
                  <Field
                    type="text"
                    label="Reference"
                    name="reference"
                    id="reference"
                    component={FormikTextInput}
                    autoComplete="off"
                    isDisabled={isSubmitting || disabledFields.includes('reference')}
                  />
                </Box>
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  childGap={{ base: 'lg', desktop: 'xl' }}
                >
                  <Field
                    type="text"
                    label="Program Reference ID"
                    name="programReferenceId"
                    id="programReferenceId"
                    component={FormikTextInput}
                    autoComplete="off"
                    isDisabled={isSubmitting || disabledFields.includes('programReferenceId')}
                  />
                  <Field
                    label="Program Type"
                    name="programType"
                    id="programType"
                    options={shownProgramTypes}
                    component={FormikSelectInputNative}
                    autoComplete="off"
                    isDisabled={programTypeDisabled || isSubmitting || disabledFields.includes('programType')}
                  />
                </Box>
              </FormSection>
              <FormSection title="Property Address">
                <AddressFormFields
                  isSubmitting={isSubmitting}
                  disabledFields={disabledFields}
                  showLatLon
                  showAddress2
                  isLatLonRequired={values.state === 'PR'}
                />
              </FormSection>
              <FormSection title="Applicant">
                <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                  <ApplicantInformation
                    isSubmitting={isSubmitting}
                    disabledFields={disabledFields}
                    prefix="primary"
                  />
                </Box>
                <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                  <AddressFormFields
                    isSubmitting={isSubmitting}
                    prefix="applicant"
                    showAddress2
                    disabledFields={disabledFields}
                    addressInheritToggleConfig={
                      isAccountCreate &&
                      ![ProgramType.newCommunity, ProgramType.newHomes].includes(values.programType)
                        ? {
                            fieldName: 'usePropAddressForMainApplicantAddress',
                            label: 'Use property address as the primary applicant address',
                            translationKey: 'usePropAddressForMainApplicantAddress',
                          }
                        : undefined
                    }
                  />
                </Box>
              </FormSection>
              <FormSection
                title="Additional Applicant"
                description="If more than one person will be applying for credit approval. This can be done later. At least one applicant must be on property title"
              >
                {!hasAdditionalApplicantData && (
                  <Toggle
                    id="addAdditionalApplicant"
                    label="Additional Applicant"
                    isChecked={showAdditionalApplicant}
                    onChange={() => {
                      setShowAdditionalApplicant(!showAdditionalApplicant);
                      setFieldValue('addAdditionalApplicant', !showAdditionalApplicant);
                    }}
                  />
                )}
                {showAdditionalApplicant && (
                  <ApplicantInformation
                    isSubmitting={isSubmitting}
                    disabledFields={disabledFields}
                    prefix="secondary"
                  />
                )}
              </FormSection>
              <FormSection
                title="Sales Representative"
                description={
                  profileAsSalesRepFeatureEnabled && isAccountCreate && user?.userIsSalesRep ? (
                    <Box childGap="xs">
                      <p>
                        To change the auto-populated Sales Rep information, visit your{' '}
                        <Link to="/profile">Profile Page</Link>.
                      </p>
                    </Box>
                  ) : (
                    ''
                  )
                }
              >
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  childGap={{ base: 'lg', desktop: 'xl' }}
                >
                  <Box flex="auto" width="100">
                    <Field
                      type="text"
                      label="Sales Rep Name"
                      name="salesRepName"
                      id="salesRepName"
                      component={FormikTextInput}
                      isRequired={values.programType !== ProgramType.consumerSolar}
                      autoComplete="off"
                      isDisabled={isSubmitting || disabledFields.includes('salesRepName')}
                    />
                  </Box>
                  <Box flex="auto" width="100" childGap="xs" justifyContent="center">
                    {isSalesRepLicenseManagedByRecheck(
                      values.state,
                      values.programType,
                      salesRepRecheckBeforeContract,
                    ) ? (
                      <>
                        {values.salesRepLicenseNumber && (
                          <Field
                            type="text"
                            label="License Number"
                            name="salesRepLicenseNumber"
                            id="salesRepLicenseNumber"
                            component={FormikTextInput}
                            autoComplete="off"
                            isDisabled
                            inputProps={{
                              readOnly: true,
                            }}
                          />
                        )}
                        <Box margin="sm 0 0 0">
                          <RecheckLicenseLink />
                        </Box>
                      </>
                    ) : (
                      <Field
                        type="text"
                        label="License Number"
                        name="salesRepLicenseNumber"
                        id="salesRepLicenseNumber"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('salesRepLicenseNumber')}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  childGap={{ base: 'lg', desktop: 'xl' }}
                >
                  <Field
                    type="email"
                    label="Sales Rep Email"
                    name="salesRepEmail"
                    id="salesRepEmail"
                    component={FormikTextInput}
                    autoComplete="off"
                    isRequired={values.programType !== ProgramType.consumerSolar}
                    isDisabled={isSubmitting || disabledFields.includes('salesRepEmail')}
                  />
                  <Field
                    type="text"
                    label="Sales Rep Phone Number"
                    name="salesRepPhoneNumber"
                    id="salesRepPhoneNumber"
                    component={FormikTextInput}
                    inputMask="phone"
                    autoComplete="off"
                    isDisabled={isSubmitting || disabledFields.includes('salesRepPhoneNumber')}
                  />
                </Box>
              </FormSection>
              {values.programType === ProgramType.geothermal && (
                <FormSection title="Property Details">
                  <Box width={{ base: '100', tablet: '30' }}>
                    <Field
                      type="number"
                      label="Home Size (sq ft)"
                      name="homeSize"
                      id="homeSize"
                      component={FormikTextInput}
                      autoComplete="off"
                      isDisabled={isSubmitting || disabledFields.includes('homeSize')}
                    />
                  </Box>
                </FormSection>
              )}
              {values.programType === ProgramType.generator && (
                <FormSection
                  title="Property Details"
                  description="Number of generator systems being installed will determine how many Systems are created in the System Design step."
                >
                  <Box width={{ base: '100', tablet: '30' }}>
                    <Field
                      type="number"
                      label="Number of Systems"
                      name="numberOfSystems"
                      id="numberOfSystems"
                      component={FormikTextInput}
                      autoComplete="off"
                      isDisabled={isSubmitting || disabledFields.includes('numberOfSystems')}
                    />
                  </Box>
                </FormSection>
              )}
              {(values.programType === ProgramType.hvac || values.programType === ProgramType.waterHeater) && (
                <FormSection
                  title="Property Details"
                  description="Number of systems being installed will determine how many Systems are created in the System Design step."
                >
                  {values.programType === ProgramType.hvac ? (
                    <Box
                      direction={{
                        base: 'column',
                        tablet: 'row',
                      }}
                      childGap={{ base: 'lg', desktop: 'xl' }}
                    >
                      <Field
                        type="number"
                        label="Home Size (sq ft)"
                        name="homeSize"
                        id="homeSize"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('homeSize')}
                      />
                      <Field
                        type="number"
                        label="Number of Existing Systems"
                        name="existingSystems"
                        id="existingSystems"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('existingSystems')}
                      />
                      <Field
                        type="number"
                        label="Number of Systems Being Installed"
                        name="systemsBeingReplaced"
                        id="systemsBeingReplaced"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('systemsBeingReplaced')}
                      />
                    </Box>
                  ) : (
                    <Box width={{ base: '100', tablet: '30' }}>
                      <Field
                        type="number"
                        label="Number of Systems Being Installed"
                        name="systemsBeingReplaced"
                        id="systemsBeingReplaced"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('systemsBeingReplaced')}
                      />
                    </Box>
                  )}
                </FormSection>
              )}
              {values.programType !== ProgramType.hvac &&
                values.programType !== ProgramType.waterHeater &&
                values.programType !== ProgramType.generator &&
                values.programType !== ProgramType.geothermal && (
                  <FormSection title="Utility Information">
                    <Box
                      direction={{
                        base: 'column',
                        tablet: 'row',
                      }}
                      childGap={{ base: 'lg', desktop: 'xl' }}
                    >
                      <UtilityNameSelectInput
                        isDisabled={disabledFields.includes('lseId') || !values.programType}
                        programType={values.programType}
                      />
                      <Field
                        type="number"
                        label="Tariff Id"
                        name="tariffId"
                        id="tariffId"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('tariffId')}
                      />
                      <Field
                        type="number"
                        label="Rate"
                        name="rate"
                        id="rate"
                        component={FormikTextInput}
                        autoComplete="off"
                        isDisabled={isSubmitting || disabledFields.includes('rate')}
                      />
                    </Box>
                  </FormSection>
                )}
              <Box
                direction={{
                  base: 'column',
                  tablet: 'row',
                }}
                alignItems={{
                  base: 'stretch',
                  tablet: 'flex-end',
                }}
                justifyContent={{
                  tablet: 'flex-end',
                }}
                childGap="sm"
                style={{ flexShrink: 0 }}
                padding="lg 0 0 0"
              >
                <Button
                  as="a"
                  navigate={() => navigate(`/accounts/${id ?? ''}`)}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!canPerformActionBasedOnNoNewSalesOrgStatus || !allowsAction}
                  size="md"
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  onClick={() => {
                    if (
                      values.usePropAddressForMainApplicantAddress &&
                      ![ProgramType.newCommunity, ProgramType.newHomes].includes(values.programType)
                    ) {
                      copyPropertyAddressToApplicant(values, setFieldValue);
                    }
                  }}
                >
                  Submit
                </Button>
              </Box>
            </EnhancedFormikForm>
          );
        }}
      </Formik>
    </>
  );
};
